import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import Cropper from "react-easy-crop";
import Dropdown from "../../Component/Common/Dropdown";
import { InputField } from "../../Component/Common/InputField";
import { checkFormError, getResposeError } from "../../helper";
import { blaBleValidation } from "../../helper/errorSchema";
import { getStorage } from "../../helper/storage";
import { addBlaBle, getBlaBleDetails, updateBlaBle } from "../../services/blaBleService";
import UserContext from "../../helper/UserContext";
import "../BlaBle/BlaBle.css";

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PLACEHOLDER_AVATAR =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Crect width='160' height='160' fill='%23eef2f7'/%3E%3Ccircle cx='80' cy='61' r='31' fill='%2395a3b8'/%3E%3Cpath d='M28 139c8-32 29-49 52-49s44 17 52 49' fill='%2395a3b8'/%3E%3C/svg%3E";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

const getCroppedImageBlob = async (imageSrc, cropAreaPixels, outputSize = 480) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, cropAreaPixels.x, cropAreaPixels.y, cropAreaPixels.width, cropAreaPixels.height, 0, 0, outputSize, outputSize);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92));
};

const basicFields = [
  { name: "name", label: "Name", required: true },
  { name: "mobile", label: "Mobile Number", required: true, maxLength: 10 },
  { name: "epic_no", label: "EPIC Number", required: true },
  { name: "assembly_no", label: "Assembly Number" },
  { name: "booth_no", label: "Booth Number" },
];
const extraFields = Array.from({ length: 10 }, (_, index) => ({ name: `extra${index + 1}`, label: `Extra ${index + 1}` }));
const descriptionFields = Array.from({ length: 5 }, (_, index) => ({ name: `description${index + 1}`, label: `Description ${index + 1}` }));

const SECTIONS = [
  { id: "basic", label: "Basic Information", fields: basicFields },
  { id: "extra", label: "Extra Information", fields: extraFields },
  { id: "description", label: "Description Information", fields: descriptionFields },
];

const initialForm = [...basicFields, ...extraFields, ...descriptionFields].reduce(
  (acc, field) => ({ ...acc, [field.name]: "" }),
  { profile_image: "", is_active: true, remove_profile_image: false }
);

const getRecordId = (item) => item?.uuid || item?.id;
const getImageUrl = (item) => item?.profile_image || item?.profile_image_url || item?.image || item?.image_url || "";
const getAssemblyNumbers = (userLogin, user) => user?.appDetails?.assembly_number || userLogin?.appDetails?.assembly_number || [];

const normalizeRecord = (item = {}) => ({
  ...initialForm,
  ...item,
  mobile: item?.mobile || item?.mobile_number || item?.mobile_no || "",
  epic_no: item?.epic_no || item?.epic_number || item?.epic || "",
  assembly_no: item?.assembly_no || item?.assembly_number || "",
  booth_no: item?.booth_no || item?.booth_number || "",
  type: item?.type || "BLA",
  gender: item?.gender || "",
  rel_name: item?.rel_name || item?.relative_name || "",
  party_name: item?.party_name || "",
  address: item?.address || "",
  profile_image: getImageUrl(item),
  is_active: item?.is_active === undefined || item?.is_active === null ? item?.status !== false : item?.is_active,
});

const buildPayload = (formValue, userLogin) => {
  const formData = new FormData();
  [...basicFields, ...extraFields, ...descriptionFields].forEach(({ name }) => {
    if (name === "assembly_no") {
      formData.append("ac_no", formValue?.assembly_no ?? "");
      formData.append("assembly_no", formValue?.assembly_no ?? "");
      return;
    }
    formData.append(name, formValue?.[name] ?? "");
  });
  formData.append("is_active", formValue?.is_active ? "true" : "false");
  if (userLogin?.app_name) formData.append("app_name", userLogin.app_name);
  if (userLogin?.party_name) formData.append("party_name", userLogin.party_name);
  if (formValue?.profile_image instanceof File) formData.append("profile_image", formValue.profile_image);
  if (formValue?.remove_profile_image) formData.append("remove_profile_image", "true");
  return formData;
};

// The "Add BLA / BLE" modal from BlaBle.js, moved to its own routed page -
// also handles Edit (reached via BoothAgentList's edit action as
// /booth-agent-management/add?editId=<id>), fetching the full record via
// getBlaBleDetails (the same single-record endpoint the old View modal
// already used).
export const AddBoothAgent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId");
  const userLogin = getStorage("logged-in");
  const { user } = useContext(UserContext);
  const fileRef = useRef(null);

  const [isEdit, setIsEdit] = useState(!!editId);
  const [formValue, setFormValue] = useState(initialForm);
  const [initialValue, setInitialValue] = useState(initialForm);
  const [formError, setFormError] = useState({});
  const [preview, setPreview] = useState(PLACEHOLDER_AVATAR);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [isLoadding, setIsLoadding] = useState(false);

  const avfBodyRef = useRef(null);
  const avfSectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const avfScrollLockRef = useRef(false);
  const avfScrollLockTimeoutRef = useRef(null);

  useEffect(() => () => window.clearTimeout(avfScrollLockTimeoutRef.current), []);

  const registerSectionRef = (id) => (el) => {
    avfSectionRefs.current[id] = el;
  };

  const scrollToSection = (id) => {
    const container = avfBodyRef.current;
    const target = avfSectionRefs.current[id];
    if (!container || !target) return;
    avfScrollLockRef.current = true;
    window.clearTimeout(avfScrollLockTimeoutRef.current);
    avfScrollLockTimeoutRef.current = window.setTimeout(() => {
      avfScrollLockRef.current = false;
    }, 600);
    container.scrollTo({ top: target.offsetTop - 8, behavior: "smooth" });
    setActiveSection(id);
  };

  const handleAvfBodyScroll = () => {
    if (avfScrollLockRef.current) return;
    const container = avfBodyRef.current;
    if (!container) return;
    const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
    if (atBottom) {
      setActiveSection(SECTIONS[SECTIONS.length - 1].id);
      return;
    }
    const scrollPos = container.scrollTop + 48;
    let current = SECTIONS[0].id;
    SECTIONS.forEach((section) => {
      const target = avfSectionRefs.current[section.id];
      if (target && target.offsetTop <= scrollPos) current = section.id;
    });
    setActiveSection(current);
  };

  const assemblyOptions = useMemo(
    () =>
      getAssemblyNumbers(userLogin, user)
        .filter(Boolean)
        .map((assemblyNumber) => ({ label: String(assemblyNumber), value: String(assemblyNumber) })),
    [userLogin, user]
  );

  const goBackToList = () => navigate("/booth-agent-management");

  useEffect(() => {
    return () => {
      if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    if (!editId) return;
    const loadRecord = async () => {
      const res = await getBlaBleDetails({ id: editId });
      if (res?.success && res?.results) {
        const record = normalizeRecord(res.results);
        setFormValue(record);
        setInitialValue(record);
        setPreview(record.profile_image || PLACEHOLDER_AVATAR);
        setIsEdit(true);
      }
    };
    loadRecord();
  }, [editId]);

  const handleOnChange = async (name, value) => {
    const stateObj = { ...formValue, [name]: value };
    setFormValue(stateObj);
    if (formError && Object.keys(formError).length) {
      const error = await checkFormError(stateObj, blaBleValidation);
      setFormError(error === true ? {} : error);
    }
  };

  const validateImage = (file) => {
    if (!file) return "";
    if (!IMAGE_TYPES.includes(file.type)) return "Only JPG, PNG, or WEBP images are allowed";
    if (file.size > MAX_IMAGE_SIZE) return "Image size must be 2 MB or less";
    return "";
  };

  const handleImageChange = (file) => {
    if (!file) return;
    const imageError = validateImage(file);
    if (imageError) {
      setFormError((current) => ({ ...current, profile_image: imageError }));
      return;
    }
    setFormError((current) => ({ ...current, profile_image: "" }));
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_, areaPixels) => setCroppedAreaPixels(areaPixels), []);

  const handleCropConfirm = async () => {
    if (!rawImageSrc || !croppedAreaPixels) return;
    const blob = await getCroppedImageBlob(rawImageSrc, croppedAreaPixels);
    const croppedFile = new File([blob], "profile.jpg", { type: "image/jpeg" });
    const nextPreview = URL.createObjectURL(blob);
    setPreview((current) => {
      if (current?.startsWith("blob:")) URL.revokeObjectURL(current);
      return nextPreview;
    });
    setFormValue((current) => ({ ...current, profile_image: croppedFile, remove_profile_image: false }));
    setCropModalOpen(false);
    setRawImageSrc(null);
  };

  const handleThumbClick = () => {
    if (preview && preview !== PLACEHOLDER_AVATAR) setPreviewModalOpen(true);
    else fileRef.current?.click();
  };

  const removeImage = () => {
    setPreview((current) => {
      if (current?.startsWith("blob:")) URL.revokeObjectURL(current);
      return PLACEHOLDER_AVATAR;
    });
    setFormValue((current) => ({ ...current, profile_image: "", remove_profile_image: true }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const onSuccessResponse = (message) => {
    toast.success(message?.message || message || "success");
    queryClient.invalidateQueries("getBlaBleList");
    setIsLoadding(false);
    goBackToList();
  };

  const onErrorResponse = (error) => {
    toast.error(getResposeError(error || "server error") || "server error");
    setIsLoadding(false);
  };

  const addMutation = useMutation(addBlaBle, {
    onSuccess: (res) => (res?.success ? onSuccessResponse(res) : onErrorResponse(res?.error || res)),
    onError: onErrorResponse,
  });

  const updateMutation = useMutation(updateBlaBle, {
    onSuccess: (res) => (res?.success ? onSuccessResponse(res) : onErrorResponse(res?.error || res)),
    onError: onErrorResponse,
  });

  const handleSubmit = async () => {
    const validationResult = await checkFormError(formValue, blaBleValidation);
    const imageError = formValue.profile_image instanceof File ? validateImage(formValue.profile_image) : "";

    if (validationResult === true && !imageError) {
      setIsLoadding(true);
      const formData = buildPayload(formValue, userLogin);
      if (isEdit) {
        updateMutation.mutate({ id: getRecordId(formValue), formData });
      } else {
        addMutation.mutate(formData);
      }
    } else {
      setIsLoadding(false);
      setFormError({ ...(validationResult === true ? {} : validationResult), ...(imageError ? { profile_image: imageError } : {}) });
    }
  };

  const renderInput = ({ name, label, required, maxLength }) => {
    if (name === "assembly_no" && assemblyOptions.length) {
      return (
        <div className="avf-field" key={name}>
          <Dropdown
            floating
            label={label}
            labelSpan={required ? "*" : ""}
            labelSpanClass="style_for_star"
            className="dropdownoforder"
            placeholder="Select Assembly Number"
            options={assemblyOptions}
            valueText="value"
            labelText="label"
            selectedOption={assemblyOptions.find((option) => option.value === String(formValue?.assembly_no)) || null}
            setSelectedOption={(option) => handleOnChange("assembly_no", option?.value || "")}
            focus={!!formError?.assembly_no}
            error={formError?.assembly_no}
            classNamePrefix="anvRc_controller"
            isClearable
          />
        </div>
      );
    }
    return (
      <div className="avf-field" key={name}>
        <InputField
          floating
          className="modal_input"
          label={label}
          labelClass="modal_label"
          labelSpan={required ? "*" : ""}
          labelSpanClass="style_for_star"
          placeholder={label}
          name={name}
          maxLength={maxLength}
          focus={!!formError?.[name]}
          error={formError?.[name]}
          value={formValue?.[name] || ""}
          onChange={({ target: { name: fieldName, value } }) => handleOnChange(fieldName, value)}
        />
      </div>
    );
  };

  const requiredFieldNames = basicFields.filter((field) => field.required).map((field) => field.name);
  const requiredErrorCount = requiredFieldNames.filter((name) => !!formError?.[name]).length;
  const submitDisabled = isEdit ? JSON.stringify(formValue) === JSON.stringify(initialValue) : false;

  return (
    <>
      <Modal isOpen toggle={goBackToList} centered className="avf-dialog" contentClassName="avf-content">
        <div className="avf-header">
          <div className="avf-header-top">
            <div>
              <h2 className="avf-title">{isEdit ? "Edit Booth Agent" : "Add Booth Agent"}</h2>
              <p className="avf-subtitle">Fill in the booth agent's details across the sections below</p>
            </div>
            <button type="button" className="avf-close-btn" onClick={goBackToList} aria-label="Close">
              ×
            </button>
          </div>
          <div className="avf-rail">
            {SECTIONS.map((section, index) => (
              <button
                key={section.id}
                type="button"
                className={`avf-step ${activeSection === section.id ? "is-active" : ""}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="avf-step-num">{index + 1}</span>
                <span className="avf-step-label">{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="avf-body" ref={avfBodyRef} onScroll={handleAvfBodyScroll}>
          <div className="avf-photo-card">
            <button
              type="button"
              className="avf-photo-avatar"
              onClick={handleThumbClick}
              title={preview && preview !== PLACEHOLDER_AVATAR ? "View full photo" : "Upload photo"}
            >
              <img
                src={preview || PLACEHOLDER_AVATAR}
                alt="Profile preview"
                onError={({ currentTarget }) => {
                  currentTarget.src = PLACEHOLDER_AVATAR;
                }}
              />
            </button>
            <div className="avf-photo-info">
              <div className="avf-photo-title">Agent photograph</div>
              <div className="avf-photo-subtitle">JPG or PNG, square crop recommended, up to 2MB</div>
              {formError?.profile_image && <div className="voter-photo-error">{formError.profile_image}</div>}
            </div>
            <div className="avf-photo-actions">
              {preview && preview !== PLACEHOLDER_AVATAR && (
                <button type="button" className="avf-photo-remove-btn" onClick={removeImage}>
                  Remove
                </button>
              )}
              <button type="button" className="avf-photo-upload-btn" onClick={() => fileRef.current?.click()}>
                {preview && preview !== PLACEHOLDER_AVATAR ? "Change photo" : "Upload photo"}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                hidden
                onChange={({ target: { files } }) => handleImageChange(files?.[0])}
              />
            </div>
          </div>

          {SECTIONS.map((section, index) => (
            <div key={section.id} className="avf-section" ref={registerSectionRef(section.id)}>
              <div className="avf-section-head">
                <span className="avf-section-num">{index + 1}</span>
                <h3 className="avf-section-title">{section.label}</h3>
              </div>
              <div className="avf-grid">{section.fields.map(renderInput)}</div>
            </div>
          ))}
        </div>

        <div className="avf-footer">
          <div className={`avf-footer-note ${requiredErrorCount > 0 ? "has-errors" : ""}`}>
            {requiredErrorCount > 0
              ? `${requiredErrorCount} of ${requiredFieldNames.length} required fields need attention`
              : `${requiredFieldNames.length} required fields`}
          </div>
          <div className="avf-footer-actions">
            <button type="button" className="avf-btn avf-btn-secondary" onClick={goBackToList}>
              Cancel
            </button>
            {isLoadding ? (
              <button type="button" className="avf-btn avf-btn-primary" disabled>
                Processing...
              </button>
            ) : (
              <button type="button" className="avf-btn avf-btn-primary" disabled={submitDisabled} onClick={handleSubmit}>
                {isEdit ? "Update" : "Add"}
              </button>
            )}
          </div>
        </div>
      </Modal>

      <Modal size="md" isOpen={cropModalOpen} toggle={() => setCropModalOpen(false)} centered>
        <ModalHeader toggle={() => setCropModalOpen(false)}>Crop Profile Photo</ModalHeader>
        <ModalBody>
          <div style={{ position: "relative", width: "100%", height: "300px" }}>
            {rawImageSrc && (
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
            <span>Zoom</span>
            <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} style={{ flex: 1 }} />
          </div>
        </ModalBody>
        <div className="bla-ble-image-actions" style={{ padding: "12px 16px", justifyContent: "flex-end", display: "flex", gap: 10 }}>
          <button type="button" className="bla-ble-remove-image" onClick={() => setCropModalOpen(false)}>
            Cancel
          </button>
          <button type="button" className="bla-ble-remove-image" onClick={handleCropConfirm}>
            Save
          </button>
        </div>
      </Modal>

      <Modal size="sm" isOpen={previewModalOpen} toggle={() => setPreviewModalOpen(false)} centered>
        <ModalHeader toggle={() => setPreviewModalOpen(false)}>Profile Photo</ModalHeader>
        <ModalBody style={{ textAlign: "center" }}>
          <img
            src={preview || PLACEHOLDER_AVATAR}
            alt="Profile full preview"
            style={{ maxWidth: "100%", borderRadius: 8 }}
            onError={({ currentTarget }) => {
              currentTarget.src = PLACEHOLDER_AVATAR;
            }}
          />
        </ModalBody>
      </Modal>
    </>
  );
};
